import React, { useEffect, useState } from "react";
import { createPortal } from "react-dom";
import "../Stylesheets/NotesModal.css";

const API = "https://worker-consolidated.maxli5004.workers.dev/record-history";

// Plain-English names for logged actions.
const ACTION_LABELS = {
  "add-client": "Client added",
  "add-lead": "Lead added",
  "edit-client": "Client edited",
  "edit-kid": "Kid edited",
  "edit-lead": "Lead edited",
  "delete-client": "Client deleted",
  "delete-kid": "Kid deleted",
  "delete-lead": "Lead deleted",
  "convert-client-to-lead": "Moved to leads",
  "convert-kid-to-lead": "Moved to leads",
  "add-note": "Note added",
  "delete-note": "Note deleted",
  "toggle-do-not-mail": "Do-not-mail changed",
  "stripe-new-membership": "Stripe sign-up",
  "stripe-renewal": "Stripe renewal",
  "stripe-cancellation": "Stripe cancellation",
  "auto-expiry-reminder": "Expiry reminder sent",
};

// Fields that change on every email/text blast; not worth showing.
const QUIET_FIELDS = new Set(["lastCustomEmailSent", "lastCustomTextSent"]);
// Fields shown from a full snapshot (add / delete).
const SNAPSHOT_FIELDS = ["startDate", "endDate", "membershipDuration", "planDescription", "belt", "phone"];

const fmtTime = (ts) =>
  new Date(ts).toLocaleString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
    hour: "numeric",
    minute: "2-digit",
  });

const fmtVal = (v) => {
  if (v === null || v === undefined || v === "") return "—";
  if (typeof v === "string") return v;
  if (Array.isArray(v) && v.every((x) => x && typeof x === "object" && "text" in x)) {
    return v.map((x) => x.text).join(" · ") || "—";
  }
  return JSON.stringify(v);
};

const fmtUser = (u) =>
  !u ? "unknown" : u.startsWith("system:") ? u.replace("system:", "automatic: ").replace(/-/g, " ") : u;

function Detail({ d }) {
  const lines = [];
  if (Array.isArray(d.changes)) {
    d.changes
      .filter((c) => !QUIET_FIELDS.has(c.field))
      .forEach((c) =>
        lines.push(
          <li key={`c-${c.field}`}>
            <b>{c.field}</b>: {fmtVal(c.from)} → {fmtVal(c.to)}
          </li>
        )
      );
    if (d.changes.length === 0) lines.push(<li key="none">Saved with no changes</li>);
  }
  if (d.record && !d.changes) {
    SNAPSHOT_FIELDS.filter((f) => d.record[f]).forEach((f) =>
      lines.push(
        <li key={`r-${f}`}>
          <b>{f}</b>: {fmtVal(d.record[f])}
        </li>
      )
    );
  }
  if (d.note) lines.push(<li key="note">“{d.note}”</li>);
  if (d.removedNote) lines.push(<li key="rnote">Removed: “{fmtVal(d.removedNote.text)}”</li>);
  if (typeof d.value === "boolean") lines.push(<li key="dnm">Do not mail: {d.value ? "on" : "off"}</li>);
  if (d.endDate && !d.changes?.some((c) => c.field === "endDate") && !d.record)
    lines.push(<li key="end">End date: {d.endDate}</li>);
  if (!lines.length && d.key) lines.push(<li key="k" className="nm-note-ts">{d.key}</li>);
  return <ul style={{ margin: "6px 0 0", paddingLeft: 18, lineHeight: 1.5 }}>{lines}</ul>;
}

/**
 * Change history for one person: everything logged against this record key
 * plus everything logged against their email (so a delete + re-add shows as
 * one timeline). Old logs from before Nov 2026 only say who/when, not what.
 */
export default function HistoryModal({ open, onClose, recordKey, email, name, buildHeaders }) {
  const [state, setState] = useState({ loading: true, error: "", rows: [] });

  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    (async () => {
      setState({ loading: true, error: "", rows: [] });
      try {
        const headers = await buildHeaders();
        const queries = [recordKey && `key=${encodeURIComponent(recordKey)}`, email && `email=${encodeURIComponent(email)}`].filter(Boolean);
        const results = await Promise.all(
          queries.map((q) => fetch(`${API}?${q}`, { headers }).then((r) => (r.ok ? r.json() : Promise.reject(r.status))))
        );
        const seen = new Set();
        const rows = results
          .flatMap((r) => r.history || [])
          .filter((l) => {
            const id = `${l.timestamp}|${l.action}`;
            if (seen.has(id)) return false;
            seen.add(id);
            return true;
          })
          .sort((a, b) => b.timestamp.localeCompare(a.timestamp));
        if (!cancelled) setState({ loading: false, error: "", rows });
      } catch {
        if (!cancelled) setState({ loading: false, error: "Could not load history.", rows: [] });
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [open, recordKey, email, buildHeaders]);

  if (!open) return null;
  const { loading, error, rows } = state;

  return createPortal(
    <div className="nm-overlay" onClick={onClose}>
      <div className="nm-modal" style={{ width: 560 }} onClick={(e) => e.stopPropagation()}>
        <div className="nm-header">
          <h3>
            History{name ? ` — ${name}` : ""} {rows.length > 0 && <span className="nm-count">{rows.length}</span>}
          </h3>
          <button className="nm-close" onClick={onClose}>✕</button>
        </div>
        <div className="nm-body">
          <div className="nm-history">
            {loading && <p className="nm-empty">Loading…</p>}
            {error && <p className="nm-error">{error}</p>}
            {!loading && !error && rows.length === 0 && <p className="nm-empty">No history recorded.</p>}
            {rows.map((l, i) => (
              <div className="nm-note" key={i}>
                <div className="nm-note-meta">
                  <span className="nm-note-author">{ACTION_LABELS[l.action] || l.action}</span>
                  <span className="nm-note-ts">
                    {fmtTime(l.timestamp)} · {fmtUser(l.user)}
                  </span>
                </div>
                <div className="nm-note-text">
                  <Detail d={l.details || {}} />
                </div>
              </div>
            ))}
            {!loading && rows.length > 0 && (
              <p className="nm-empty" style={{ fontSize: 12 }}>
                Edits before Nov 5, 2026 only recorded who and when, not what changed.
              </p>
            )}
          </div>
        </div>
      </div>
    </div>,
    document.body
  );
}
